import { useRef, useState } from "react";
import {
  PRIO,
  nlsfbClamp,
  nlsfbLabel,
  priorityOf,
  Q,
  suggestElements,
  suggestRobaws,
  TYPES,
  type Answers,
  type BuildingDto,
  type IssueDetail,
  type QuestionKey,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { isVideo, queuePhoto, thumbUrl } from "../../lib/photos";
import { useMe } from "../../lib/auth";
import { createIssue } from "../../offline/issueActions";
import { ElementPicker } from "../elements/ElementPicker";
import { AssetBadge } from "../assets/assetIcons";
import { recentElements, rememberElement } from "../elements/recent";
import { useToast } from "../../lib/toast";
import { MediaThumb } from "./MediaThumb";

type Draft = {
  title: string;
  description: string;
  answers: Partial<Answers>;
  floorId: string;
  roomId: string;
  installationId: string;
  elementCode: string | null;
  pin: { x: number; y: number } | null;
  photos: string[];
};

const EMPTY_ANSWERS: Partial<Answers> = { size: "klein", solver: "kluswacht", origin: "ronde" };

export function Choices({
  label,
  value,
  opts,
  onPick,
  required,
}: {
  label: string;
  value: string | undefined;
  opts: readonly { k: string; l: string; h?: string }[];
  onPick: (k: string) => void;
  required?: boolean;
}) {
  return (
    <fieldset className="q">
      <legend className="ql">
        {label}
        {required && " *"}
      </legend>
      <div className="choices">
        {opts.map((o) => (
          <button type="button" key={o.k} aria-pressed={value === o.k} onClick={() => onPick(o.k)}>
            {o.l}
            {o.h && <small>{o.h}</small>}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Nieuwe bevinding (met of zonder pin) of bevinding bewerken. */
export function IssueForm({
  building: b,
  issue,
  initial,
  onClose,
  onSaved,
}: {
  building: BuildingDto;
  issue?: IssueDetail;
  initial?: {
    floorId?: string;
    roomId?: string | null;
    pin?: { x: number; y: number } | null;
    /** Bevinding bij een asset (bv. vanuit het assetpaneel). */
    installationId?: string;
  };
  onClose: () => void;
  /** `queued`: alleen op dit apparaat opgeslagen, wordt verstuurd zodra er verbinding is. */
  onSaved: (d: IssueDetail, queued?: boolean) => void;
}) {
  const main = issue?.locations[0];
  const [d, setD] = useState<Draft>(() => ({
    title: issue?.title ?? "",
    description: issue?.description ?? "",
    answers: issue ? { ...issue.answers } : { ...EMPTY_ANSWERS },
    floorId: main?.floorId ?? initial?.floorId ?? b.floors[0]?.id ?? "",
    roomId: main?.roomId ?? initial?.roomId ?? "",
    installationId: issue?.installationId ?? initial?.installationId ?? "",
    elementCode: issue?.elementCode ?? null,
    pin: main && main.x != null && main.y != null ? { x: main.x, y: main.y } : (initial?.pin ?? null),
    photos: [],
  }));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { data: me } = useMe();

  const a = d.answers;
  const p = priorityOf(a);
  const sug = suggestRobaws(a);
  const missing = !d.title.trim() || !a.type || !a.danger || !a.impact || !a.worsen;
  const pick = (k: QuestionKey | "type") => (v: string) => setD({ ...d, answers: { ...d.answers, [k]: v } });

  const full = me?.org?.elementCoding ?? false;
  const asset = b.assets.find((x) => x.id === d.installationId);
  // Voorstel: code van de asset, trefwoorden uit de tekst, dan het type (docs/NLSFB.md).
  const elementSuggestions = suggestElements(
    { type: a.type, text: `${d.title} ${d.description}`, assetCode: asset?.elementCode },
    { full },
  );
  const [recent] = useState(() => recentElements(b.id).map((c) => nlsfbClamp(c, full) ?? c));

  const rooms = b.rooms.filter((r) => r.floorId === d.floorId);
  const gRoom = d.roomId ? b.assets.filter((x) => x.roomId === d.roomId) : [];
  const gFloor = b.assets.filter((x) => !gRoom.includes(x) && x.floorId === d.floorId);
  const gRest = b.assets.filter((x) => !gRoom.includes(x) && !gFloor.includes(x));

  function chooseInstallation(id: string) {
    const x = b.assets.find((i) => i.id === id);
    // Asset kiezen vult verdieping en ruimte in.
    if (x?.floorId)
      setD({
        ...d,
        installationId: id,
        floorId: x.floorId,
        roomId: x.roomId ?? "",
        pin: x.floorId === d.floorId ? d.pin : null,
      });
    else setD({ ...d, installationId: id });
  }

  async function addPhotos(list: FileList | null) {
    for (const f of [...(list ?? [])]) {
      setUploading((n) => n + 1);
      try {
        // Via de wachtrij: werkt ook zonder verbinding (SPEC §11).
        const id = await queuePhoto(f, b.id);
        setD((cur) => ({ ...cur, photos: [...cur.photos, id] }));
      } catch (e) {
        toast(e instanceof ApiError ? e.message : "Dit bestand kon niet worden verwerkt.");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }

  async function save() {
    setBusy(true);
    rememberElement(b.id, d.elementCode);
    setError(null);
    try {
      let out: IssueDetail;
      if (issue) {
        out = await api<IssueDetail>(`/issues/${issue.id}`, {
          method: "PATCH",
          body: {
            title: d.title,
            description: d.description,
            answers: a,
            installationId: d.installationId || null,
            elementCode: d.elementCode,
          },
        });
        // Locatie wijzigen = hoofdplek aanpassen.
        if (main && (main.floorId !== d.floorId || (main.roomId ?? "") !== d.roomId) && d.floorId) {
          out = await api<IssueDetail>(`/issues/${issue.id}/locations/${main.id}`, {
            method: "PATCH",
            body: {
              floorId: d.floorId,
              roomId: d.roomId || null,
              ...(main.floorId !== d.floorId ? { x: null, y: null } : {}),
            },
          });
        } else if (!main && d.floorId) {
          out = await api<IssueDetail>(`/issues/${issue.id}/locations`, {
            method: "POST",
            body: { floorId: d.floorId, roomId: d.roomId || null },
          });
        }
      } else {
        const r = await createIssue(
          b,
          {
            id: crypto.randomUUID(),
            title: d.title.trim(),
            description: d.description.trim() || null,
            answers: a as Answers,
            installationId: d.installationId || null,
            elementCode: d.elementCode,
            locations: d.floorId
              ? [{ floorId: d.floorId, roomId: d.roomId || null, x: d.pin?.x ?? null, y: d.pin?.y ?? null }]
              : [],
            photos: d.photos,
          },
          { id: me?.user.id ?? "", name: me?.user.name ?? me?.user.email ?? "" },
        );
        onSaved(r.issue, r.queued);
        return;
      }
      onSaved(out);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Opslaan lukte niet");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={issue ? `Bewerk ${issue.code}` : "Nieuwe bevinding"}
      wide
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className={`chip prio prio-${p}`} data-testid="form-priority">
            {p} · {PRIO[p].l}
          </span>
          <span className="hint">
            {PRIO[p].t}
            {sug && ` · advies: Robaws ${sug === "offer" ? "offerte" : "order"}`}
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={missing || busy || uploading > 0}>
            {busy ? "Opslaan…" : issue ? "Opslaan" : "Vastleggen"}
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      {d.pin && !issue && <p className="chip">Op tekening</p>}
      <p className="label">Wat zie je?</p>
      <label className="field">
        <span>Korte omschrijving *</span>
        <input
          value={d.title}
          maxLength={120}
          onChange={(e) => setD({ ...d, title: e.target.value })}
          placeholder="bijv. Lekkage onder spoelbak pantry"
        />
      </label>
      <label className="field">
        <span>Toelichting</span>
        <textarea
          rows={3}
          value={d.description}
          onChange={(e) => setD({ ...d, description: e.target.value })}
          placeholder="Wat is er precies aan de hand? Maten, merk, storingscode…"
        />
      </label>
      {!issue && (
        <div className="q">
          <span className="ql">Foto's en video's</span>
          <div className="photos">
            {d.photos.map((id) => (
              <div className="ph" key={id}>
                <MediaThumb fileId={id} alt={isVideo(id) ? "Nieuwe video" : "Nieuwe foto"} />
              </div>
            ))}
            {uploading > 0 && <div className="ph loading">Uploaden…</div>}
            <button type="button" className="add" onClick={() => photoInput.current?.click()}>
              +<br />
              Foto
            </button>
            <button type="button" className="add" onClick={() => videoInput.current?.click()}>
              +<br />
              Video
            </button>
          </div>
          {/* Zonder capture: op de telefoon kiest de gebruiker zelf tussen opnemen en een bestaande foto of video. */}
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            data-testid="form-photo-input"
            onChange={(e) => {
              void addPhotos(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={videoInput}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/*"
            multiple
            hidden
            data-testid="form-video-input"
            onChange={(e) => {
              void addPhotos(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      )}

      <p className="label">Paar vragen</p>
      <Choices label="Wat voor soort bevinding?" required value={a.type} opts={TYPES} onPick={pick("type")} />
      <div className="q">
        <span className="ql" id="element-label">
          Element (NLBE-SfB)
        </span>
        <ElementPicker
          value={d.elementCode}
          onChange={(elementCode) => setD({ ...d, elementCode })}
          full={full}
          suggestions={elementSuggestions}
          recent={recent}
          emptyLabel="Geen element"
        />
      </div>
      <Choices label={Q.danger.q} required value={a.danger} opts={Q.danger.opts} onPick={pick("danger")} />
      <Choices label={Q.impact.q} required value={a.impact} opts={Q.impact.opts} onPick={pick("impact")} />
      <Choices label={Q.worsen.q} required value={a.worsen} opts={Q.worsen.opts} onPick={pick("worsen")} />
      <Choices label={Q.size.q} value={a.size} opts={Q.size.opts} onPick={pick("size")} />
      <Choices label={Q.solver.q} value={a.solver} opts={Q.solver.opts} onPick={pick("solver")} />
      <Choices label={Q.origin.q} value={a.origin} opts={Q.origin.opts} onPick={pick("origin")} />

      <p className="label">Waar?</p>
      <div className="grid3">
        <label className="field">
          <span>Verdieping</span>
          <select value={d.floorId} onChange={(e) => setD({ ...d, floorId: e.target.value, roomId: "", pin: null })}>
            <option value="">—</option>
            {b.floors.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Ruimte</span>
          <select value={d.roomId} onChange={(e) => setD({ ...d, roomId: e.target.value })}>
            <option value="">—</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Asset</span>
          <select value={d.installationId} onChange={(e) => chooseInstallation(e.target.value)}>
            <option value="">—</option>
            {(
              [
                ["In deze ruimte", gRoom],
                ["Op deze verdieping", gFloor],
                ["Overige", gRest],
              ] as const
            )
              .filter(([, g]) => g.length)
              .map(([label, g]) => (
                <optgroup key={label} label={label}>
                  {g.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.code} {x.name}
                    </option>
                  ))}
                </optgroup>
              ))}
          </select>
        </label>
      </div>
      {asset && (
        <div className="assetpick" data-testid="form-asset">
          {asset.photoId ? (
            <img src={thumbUrl(asset.photoId)} alt={`Foto van ${asset.code} ${asset.name}`} />
          ) : (
            <AssetBadge code={asset.elementCode} size={40} type={asset.assetType} />
          )}
          <span className="grow">
            <strong>
              {asset.code} {asset.name}
            </strong>
            <span className="muted small block">
              {[asset.brand, asset.model].filter(Boolean).join(" ") || nlsfbLabel(asset.elementCode)}
            </span>
          </span>
        </div>
      )}
    </Modal>
  );
}
